import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth.store';

/** Progresso emitido pelos workers (BullMQ) — espelha ProgressEvent da API. */
export interface JobProgressEvent {
  jobId: string;
  kind: string;
  status: 'started' | 'progress' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  payload?: unknown;
}

export interface EntityUpdatedEvent {
  resourceType: string;
  resourceId: string;
}

export interface RealtimeNotification {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  link?: string | null;
  createdAt: string;
}

let socket: Socket | null = null;
let currentToken: string | null = null;

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? '';

/** Conexão única no namespace /realtime, autenticada com o access token. */
export function getSocket(): Socket | null {
  const token = useAuthStore.getState().accessToken;
  if (!token) {
    disconnectSocket();
    return null;
  }

  if (socket && currentToken === token) return socket;

  disconnectSocket();
  currentToken = token;
  socket = io(`${SOCKET_URL}/realtime`, {
    // `auth` como FUNÇÃO é avaliada a cada tentativa de conexão, inclusive nas
    // reconexões. Passar um objeto fixo congela o token do momento da conexão:
    // quando ele expira (30 min), toda reconexão falha com o token velho e o
    // console enche de "WebSocket connection failed". Assim, cada tentativa usa
    // o token vigente, já renovado pelo interceptor do axios.
    auth: (cb) => cb({ token: useAuthStore.getState().accessToken }),
    transports: ['websocket', 'polling'],
    reconnectionAttempts: 10,
    reconnectionDelay: 1500,
  });
  return socket;
}

export function disconnectSocket(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  currentToken = null;
}

interface RealtimeHandlers {
  onProgress?: (event: JobProgressEvent) => void;
  onNotification?: (event: RealtimeNotification) => void;
  onEntityUpdated?: (event: EntityUpdatedEvent) => void;
}

/**
 * Assina os eventos do namespace /realtime. Opcionalmente entra na sala de um
 * cliente específico (`subscribe:client`).
 */
export function useRealtime(handlers: RealtimeHandlers = {}, clientId?: string | null) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [connected, setConnected] = useState(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!accessToken) {
      setConnected(false);
      return;
    }

    const instance = getSocket();
    if (!instance) return;

    const handleConnect = () => {
      setConnected(true);
      if (clientId) instance.emit('subscribe:client', { clientId });
    };
    const handleDisconnect = () => setConnected(false);
    const handleProgress = (event: JobProgressEvent) => handlersRef.current.onProgress?.(event);
    const handleNotification = (event: RealtimeNotification) =>
      handlersRef.current.onNotification?.(event);
    const handleEntity = (event: EntityUpdatedEvent) => handlersRef.current.onEntityUpdated?.(event);

    instance.on('connect', handleConnect);
    instance.on('disconnect', handleDisconnect);
    instance.on('job:progress', handleProgress);
    instance.on('notification', handleNotification);
    instance.on('entity:updated', handleEntity);

    if (instance.connected) handleConnect();

    return () => {
      instance.off('connect', handleConnect);
      instance.off('disconnect', handleDisconnect);
      instance.off('job:progress', handleProgress);
      instance.off('notification', handleNotification);
      instance.off('entity:updated', handleEntity);
    };
  }, [accessToken, clientId]);

  return { connected };
}
